import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { map } from 'rxjs';
import { environment } from '../../environments/environment';

/** Sent on Insert/Update. */
export interface RoMasterDto {
  id?: number;
  roCode: string;
  roName: string;
  clientId: number;
  zoneId?: number | null;
  /** @deprecated primary state — the backend derives it from stateIds[0]. */
  stateId?: number | null;
  /** All states this RO covers (many-to-many). One RO can span multiple states. */
  stateIds?: number[];
  masterPolicyId?: number | null;
  isActive: boolean;
}

/** List/detail shape — includes joined names for display. */
export interface RoMaster extends RoMasterDto {
  id: number;
  zoneName?: string | null;
  stateName?: string | null;         // primary state name
  stateNames?: string | null;        // all states, comma-joined (display)
  masterPolicyName?: string | null;
  masterPolicyNumber?: string | null;
  clientName?: string | null;
  clientCode?: string | null;
}

/** "3,7,12" → [3,7,12] (defensive: handles PascalCase/camelCase + empties). */
function parseIds(csv: any): number[] {
  return String(csv ?? '')
    .split(',')
    .map((s) => Number(s.trim()))
    .filter((n) => Number.isFinite(n) && n > 0);
}

/** GetAll/GetById return PascalCase (Dapper dynamic) — map to the camelCase UI model. */
function mapRo(x: any): RoMaster {
  return {
    id: x.Id ?? x.id,
    roCode: x.RoCode ?? x.roCode ?? '',
    roName: x.RoName ?? x.roName ?? '',
    clientId: x.ClientId ?? x.clientId,
    zoneId: x.ZoneId ?? x.zoneId ?? null,
    stateId: x.StateId ?? x.stateId ?? null,
    stateIds: parseIds(x.StateIdsCsv ?? x.stateIdsCsv),
    masterPolicyId: x.MasterPolicyId ?? x.masterPolicyId ?? null,
    isActive: (x.IsActive ?? x.isActive) ?? true,
    zoneName: x.ZoneName ?? x.zoneName ?? null,
    stateName: x.StateName ?? x.stateName ?? null,
    stateNames: x.StateNames ?? x.stateNames ?? null,
    masterPolicyName: x.MasterPolicyName ?? x.masterPolicyName ?? null,
    masterPolicyNumber: x.MasterPolicyNumber ?? x.masterPolicyNumber ?? null,
    clientName: x.ClientName ?? x.clientName ?? null,
    clientCode: x.ClientCode ?? x.clientCode ?? null,
  };
}

function mapEnvelope(res: any) {
  if (Array.isArray(res?.data)) return { ...res, data: res.data.map(mapRo) };
  if (res?.data && typeof res.data === 'object') return { ...res, data: mapRo(res.data) };
  if (Array.isArray(res)) return res.map(mapRo);
  return res;
}

@Injectable({ providedIn: 'root' })
export class RoMasterService {
  private http = inject(HttpClient);
  private readonly baseUrl = `${environment.apiUrl}/RoMaster`;

  /** All ROs, optionally filtered to one client. */
  getAll(clientId?: number) {
    const params = clientId ? { clientId } : undefined;
    return this.http.get(`${this.baseUrl}/GetAll`, { params }).pipe(map(mapEnvelope));
  }
  insert(dto: RoMasterDto) {
    return this.http.post(`${this.baseUrl}/Insert`, dto);
  }
  update(dto: RoMasterDto) {
    return this.http.post(`${this.baseUrl}/Update`, dto);
  }
  setActive(id: number, isActive: boolean) {
    return this.http.get(`${this.baseUrl}/SetActive`, { params: { id, isActive } });
  }
  delete(id: number) {
    return this.http.get(`${this.baseUrl}/Delete`, { params: { id } });
  }
}
